import React, { useEffect, useState, useRef } from 'react';

/**
 * Fixed side indicator that shows how far the user has scrolled
 * through the landing page (0 – 100 %).
 *
 * Stays hidden until there is actually something to scroll so it
 * never sits on top of a short / empty page.
 */
export default function ScrollProgress({ hidden = false }) {
  const [pct, setPct] = useState(0);
  const [scrollable, setScrollable] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    const compute = () => {
      const doc = document.documentElement;
      const total = (doc.scrollHeight || 0) - window.innerHeight;
      if (total <= 40) {
        setScrollable(false);
        setPct(0);
        return;
      }
      setScrollable(true);
      const p = Math.min(100, Math.max(0, (window.scrollY / total) * 100));
      setPct(p);
    };
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        compute();
      });
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', compute);
    const interval = setInterval(compute, 1000); // catch dynamic height changes (images, accordions)
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', compute);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearInterval(interval);
    };
  }, []);

  if (hidden || !scrollable) return null;

  const rounded = Math.round(pct);

  return (
    <div
      className="fixed right-2 sm:right-3 md:right-5 top-1/2 -translate-y-1/2 z-40 hidden sm:flex flex-col items-center gap-2 pointer-events-none select-none"
      data-testid="scroll-progress"
      aria-hidden="true"
    >
      <div className="relative w-8 h-8 rounded-full bg-black text-white flex items-center justify-center shadow-lg">
        <span className="text-[10px] font-semibold tabular-nums" data-testid="scroll-progress-pct">
          {rounded}%
        </span>
      </div>
      <div className="relative w-[3px] h-[180px] md:h-[220px] rounded-full bg-black/15 overflow-hidden">
        <div
          className="absolute left-0 right-0 top-0 bg-black rounded-full transition-[height] duration-150 ease-out"
          style={{ height: `${pct}%` }}
        />
      </div>
      <div className="text-[9px] tracking-[0.25em] font-medium text-black/70 [writing-mode:vertical-rl] rotate-180">
        SCROLL
      </div>
    </div>
  );
}
